import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../ui/icons';
import { useCall } from './CallProvider';
import { useFloatingWindow } from './useFloatingWindow';

const COLLAPSED_SIZE = { width: 280, height: 40 };

/**
 * A shared screen gets a window of its own.
 *
 * It is deliberately not pinned to a corner of the board and not part of the
 * call window: what is on someone's screen is usually the main thing in the
 * room, so it needs to be movable, resizable, and able to leave the page
 * entirely — full screen, or picture-in-picture, which puts it in a real OS
 * window that survives switching tabs.
 */
export function ScreenShareWindow({ boardId }: { boardId: string }) {
  const call = useCall();
  const share = call.screenShare;
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const { rect, dragging, dragHandlers, resizeHandlers } = useFloatingWindow(
    `kanvas.screenshare-window.${boardId}`,
    {
      defaultRect: ({ width }) => ({
        x: Math.max(12, Math.round(width / 2 - 320)),
        y: 76,
        width: 640,
        height: 400,
      }),
      minWidth: 280,
      minHeight: 180,
      clampSize: collapsed ? COLLAPSED_SIZE : undefined,
    },
  );

  const track = share?.track ?? null;
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !track || collapsed) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track, collapsed]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === frameRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void frameRef.current?.requestFullscreen().catch(() => undefined);
  }, []);

  const togglePip = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (document.pictureInPictureElement) void document.exitPictureInPicture().catch(() => undefined);
    else void el.requestPictureInPicture().catch(() => undefined);
  }, []);

  if (!share) return null;

  const pipSupported = document.pictureInPictureEnabled;

  return (
    <div
      ref={frameRef}
      className={`screenshare-window ${collapsed ? 'is-collapsed' : ''} ${dragging ? 'is-dragging' : ''} ${
        fullscreen ? 'is-fullscreen' : ''
      }`}
      style={
        fullscreen
          ? undefined
          : collapsed
            ? { left: rect.x, top: rect.y, width: COLLAPSED_SIZE.width }
            : { left: rect.x, top: rect.y, width: rect.width, height: rect.height }
      }
      role="group"
      aria-label="Демонстрация экрана"
    >
      <header className="screenshare-bar" {...dragHandlers}>
        <span className="screenshare-title">
          <span className="call-live-dot is-live" />
          {share.isLocal ? 'Вы демонстрируете экран' : `${share.name} демонстрирует экран`}
        </span>

        <div className="screenshare-actions">
          {share.isLocal && (
            <button
              type="button"
              className="screenshare-stop"
              onClick={() => void call.toggleScreenShare()}
              data-tip="Остановить демонстрацию"
            >
              Остановить
            </button>
          )}
          {pipSupported && !collapsed && (
            <button
              type="button"
              className="call-bar-btn"
              onClick={togglePip}
              aria-label="Открыть в отдельном окне"
              data-tip="Отдельное окно"
            >
              <Icon name="copy" size={14} />
            </button>
          )}
          {!collapsed && (
            <button
              type="button"
              className="call-bar-btn"
              onClick={toggleFullscreen}
              aria-label={fullscreen ? 'Выйти из полноэкранного режима' : 'На весь экран'}
              data-tip={fullscreen ? 'Выйти из полного экрана' : 'На весь экран'}
            >
              <Icon name={fullscreen ? 'minimize' : 'maximize'} size={14} />
            </button>
          )}
          {!fullscreen && (
            <button
              type="button"
              className="call-bar-btn"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Показать демонстрацию' : 'Свернуть демонстрацию'}
              data-tip={collapsed ? 'Показать' : 'Свернуть'}
            >
              <Icon name={collapsed ? 'chevronDown' : 'minimize'} size={14} />
            </button>
          )}
        </div>
      </header>

      {!collapsed && (
        <div className="screenshare-body">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video ref={videoRef} autoPlay playsInline muted className="screenshare-video" />
        </div>
      )}

      {!collapsed && !fullscreen && <span className="window-resize" aria-hidden="true" {...resizeHandlers} />}
    </div>
  );
}
